import { useState } from 'react';
import { MapPin, Navigation, Search, ExternalLink, Truck } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { PlantHireCard, HIRE_TYPES } from '@/components/PlantHireCard';

export default function PlantHirePage() {
  const [postcode, setPostcode] = useState('');
  const [radius, setRadius] = useState(10);
  const [loc, setLoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState(null);
  const [type, setType] = useState('all');

  const runSearch = async (coords, label, r = radius) => {
    setLoading(true);
    setLoc({ ...coords, label });
    try {
      const { data } = await api.get('/plant-hire/nearby', { params: { lat: coords.lat, lng: coords.lng, radius: r } });
      setPlaces(data.places);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load hire depots');
      setPlaces(null);
    } finally { setLoading(false); }
  };

  const onPostcode = async (e) => {
    e.preventDefault();
    if (!postcode.trim()) { toast.error('Enter a postcode'); return; }
    setLoading(true);
    try {
      const g = await api.get('/geo/postcode', { params: { q: postcode } });
      await runSearch({ lat: g.data.lat, lng: g.data.lng }, g.data.postcode);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Postcode not found');
      setLoading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Location not supported on this device'); return; }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => runSearch({ lat: pos.coords.latitude, lng: pos.coords.longitude }, 'your location'),
      () => { toast.error('Could not get your location — try a postcode'); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const filter = HIRE_TYPES.find((t) => t.id === type);
  const shown = (places || []).filter((p) => !filter.re || filter.re.test(p.name));
  const mapsMore = loc ? `https://www.google.com/maps/search/plant+hire/@${loc.lat},${loc.lng},12z` : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Plant Hire Near Me — Find Local Plant & Tool Hire Depots"
        description="Find the nearest plant hire, tool hire, access platform and skip hire depots near you, with phone numbers, opening hours and directions. Free for UK tradesmen."
        path="/plant-hire" kicker="Plant Hire Near You" />
      <Toaster position="top-center" richColors />

      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">UK-wide · free</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight mt-1">Plant hire near you</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">Find the nearest plant, tool, access and skip hire depots, with phone numbers, opening hours and one-tap directions.</p>
      </div>

      <div className="tm-card p-5 mb-6">
        <form onSubmit={onPostcode} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="tm-input pl-9" placeholder="Enter postcode (e.g. M1 1AE)" value={postcode}
                onChange={(e) => setPostcode(e.target.value)} data-testid="plant-hire-postcode-input" />
            </div>
            <button type="submit" className="tm-btn" disabled={loading} data-testid="plant-hire-search-btn"><Search size={16} /> Search</button>
          </div>
          <button type="button" onClick={useMyLocation} disabled={loading} className="tm-btn tm-btn-ghost" data-testid="plant-hire-my-location-btn">
            <Navigation size={16} /> Use my location
          </button>
        </form>
        <label className="mt-4 flex items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-slate-500 whitespace-nowrap font-semibold">Within {radius} mi</span>
          <input type="range" min="1" max="30" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            onMouseUp={() => loc && runSearch(loc, loc.label)} onTouchEnd={() => loc && runSearch(loc, loc.label)}
            onKeyUp={(e) => loc && e.key.startsWith('Arrow') && runSearch(loc, loc.label)}
            className="flex-1 accent-brand" data-testid="plant-hire-radius-slider" />
        </label>
      </div>

      {loading && (
        <p className="text-slate-500 text-sm" data-testid="plant-hire-loading">Searching hire depots… the first search in a new area can take up to 10 seconds.</p>
      )}

      {!loading && places && (
        <div data-testid="plant-hire-results">
          <div className="flex flex-wrap gap-2 mb-4" data-testid="plant-hire-type-filters">
            {HIRE_TYPES.map((t) => {
              const n = t.re ? places.filter((p) => t.re.test(p.name)).length : places.length;
              return (
                <button key={t.id} type="button" onClick={() => setType(t.id)} data-testid={`plant-hire-type-${t.id}`}
                  className={`rounded-full px-3 py-1.5 border text-xs font-semibold transition-colors ${type === t.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'}`}>
                  {t.label} ({n})
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mb-3" data-testid="plant-hire-count">{shown.length} depot{shown.length === 1 ? '' : 's'} within {radius} miles of {loc?.label}, nearest first</p>
          {shown.length === 0 ? (
            <div className="tm-card p-8 text-center text-slate-500" data-testid="plant-hire-empty">No hire depots found here. Try a wider radius or another category.</div>
          ) : (
            <ul className="space-y-3">{shown.map((p, i) => <PlantHireCard key={p.id} p={p} index={i} />)}</ul>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
            <span>Listings © OpenStreetMap contributors. Always ring ahead to check stock and opening times.</span>
            {mapsMore && (
              <a href={mapsMore} target="_blank" rel="noreferrer" className="font-semibold text-brand hover:underline flex items-center gap-1" data-testid="plant-hire-more-google">
                Search more on Google Maps <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      )}

      {!loading && !places && (
        <div className="tm-card p-10 text-center" data-testid="plant-hire-intro">
          <Truck size={32} strokeWidth={1.5} className="mx-auto text-brand" />
          <p className="mt-3 text-slate-600">Enter a postcode or tap &ldquo;Use my location&rdquo; to find hire depots near you.</p>
        </div>
      )}
    </div>
  );
}
